import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Lock, Send } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  ClientMessageRow,
  fetchClientMessages,
  markClientMessagesReadByClient,
  sendClientMessage,
} from '../lib/instantData'
import GlassCard from '../components/ui/GlassCard'
import GradientButton from '../components/ui/GradientButton'
import PanelShell from '../components/ui/PanelShell'

const formatTimestamp = (createdAt: number) => {
  const date = new Date(createdAt)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
    ' · ' +
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const MessageCassie: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [messages, setMessages] = useState<ClientMessageRow[]>([])
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(0)

  const loadMessages = useCallback(async () => {
    if (!user?.id) return
    try {
      const rows = await fetchClientMessages(user.id)
      setMessages(rows)
      markClientMessagesReadByClient(user.id).catch(() => {})
    } catch (err) {
      console.error('Failed to load messages:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadMessages()
    const interval = setInterval(loadMessages, 5000)
    return () => clearInterval(interval)
  }, [loadMessages])

  useEffect(() => {
    // Only auto-scroll when a new message actually arrives (or on first
    // load), not on every background refresh — otherwise it fights with
    // the person trying to scroll up to read earlier messages.
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCountRef.current = messages.length
  }, [messages])

  const handleSend = async () => {
    if (!draft.trim() || !user?.id) return
    setSending(true)
    setErrorMessage('')
    try {
      await sendClientMessage({
        userId: user.id,
        senderName: user.user_metadata?.full_name || 'Client',
        content: draft.trim(),
      })
      setDraft('')
      await loadMessages()
    } catch (err) {
      console.error('Failed to send message:', err)
      setErrorMessage('Something went wrong sending your message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <PanelShell
      eyebrow="Private coaching support"
      title="Message Cassie"
      subtitle="A private conversation just between you and Cassie — not visible to anyone else."
      actions={
        <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
          Back to dashboard
        </GradientButton>
      }
    >
      <GlassCard hover={false} padded={false}>
        <div className="flex flex-col h-[65vh] min-h-[420px]">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100 text-xs text-gray-500">
            <Lock size={14} />
            <span>Private conversation — only you and Cassie can see this.</span>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500">
                  Send Cassie a message to get started — questions about training, nutrition, progress, or anything else.
                </p>
              </div>
            ) : (
              messages.map((message) => {
                const isClient = message.senderRole === 'client'
                return (
                  <div
                    key={message.id}
                    className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-md ${isClient ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div
                        className={`px-4 py-3 rounded-2xl shadow-sm ${
                          isClient
                            ? 'bg-ink text-white'
                            : 'bg-cream text-textPrimary border border-gray-100'
                        }`}
                      >
                        {!isClient && (
                          <p className="text-xs font-semibold mb-1 text-gold-dark">Cassie</p>
                        )}
                        <p className="whitespace-pre-wrap break-words">{message.content}</p>
                      </div>
                      <span className="text-xs text-gray-400 mt-1 px-1">
                        {formatTimestamp(message.createdAt)}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-6 py-4 border-t border-gray-100">
            {errorMessage && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {errorMessage}
              </div>
            )}
            <div className="flex items-end gap-3">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a message to Cassie..."
                rows={2}
                className="form-input flex-1 resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                disabled={sending}
              />
              <GradientButton
                onClick={handleSend}
                disabled={!draft.trim() || sending}
                icon={Send}
                size="sm"
              >
                {sending ? 'Sending...' : 'Send'}
              </GradientButton>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Need a scheduled check-in instead? Use{' '}
              <button
                type="button"
                onClick={() => navigate('/dashboard/request-check-in')}
                className="underline hover:text-gray-600"
              >
                Request a Coaching Check-In
              </button>
              .
            </p>
          </div>
        </div>
      </GlassCard>
    </PanelShell>
  )
}

export default MessageCassie
